import { useState } from 'react'
import { useAuth } from '@/context/auth-context'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { User, Mail, Phone, Shield, ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'

export function CounsellorProfilePage() {
    const { user } = useAuth()
    const [isEditing, setIsEditing] = useState(false)

    // Mock editable fields (in a real app, these would update context/backend)
    const [name, setName] = useState(user?.name || '')
    const [phone, setPhone] = useState('9876543210')

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-6">
            <div className="w-full max-w-2xl space-y-6">
                <Link to="/counsellor/dashboard" className="inline-flex items-center gap-2 text-theme-secondary hover:text-primary-blue transition-colors">
                    <ArrowLeft className="h-4 w-4" /> Back to Dashboard
                </Link>

                <GlassmorphicCard className="p-8">
                    <div className="text-center mb-8">
                        <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-primary-blue to-indigo-600 flex items-center justify-center text-white text-3xl font-bold mb-4 shadow-lg shadow-blue-500/30">
                            {user?.avatar || name.charAt(0)}
                        </div>
                        <h1 className="text-2xl font-bold text-theme-primary">{name}</h1>
                        <p className="text-theme-secondary">{user?.role === 'counsellor' ? 'Education Counsellor' : user?.role}</p>
                    </div>

                    <div className="space-y-6">
                        <div className="space-y-4">
                            <h3 className="text-sm font-semibold text-theme-muted uppercase tracking-wider border-b border-slate-200 dark:border-slate-700 pb-2">
                                Personal Information
                            </h3>

                            <div className="grid gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs text-theme-secondary flex items-center gap-1">
                                        <User className="h-3 w-3" /> Full Name
                                    </label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        disabled={!isEditing}
                                        className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary disabled:opacity-75 disabled:cursor-not-allowed"
                                    />
                                </div>

                                <div className="space-y-1">
                                    <label className="text-xs text-theme-secondary flex items-center gap-1">
                                        <Mail className="h-3 w-3" /> Email Address (Login ID)
                                    </label>
                                    <input
                                        type="email"
                                        value={user?.email || ''}
                                        disabled
                                        className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-theme-muted cursor-not-allowed"
                                    />
                                    <p className="text-[10px] text-theme-muted pl-1">Cannot be changed. Contact admin for updates.</p>
                                </div>

                                <div className="space-y-1">
                                    <label className="text-xs text-theme-secondary flex items-center gap-1">
                                        <Phone className="h-3 w-3" /> Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        value={phone}
                                        onChange={e => setPhone(e.target.value)}
                                        disabled={!isEditing}
                                        className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary disabled:opacity-75 disabled:cursor-not-allowed"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4 pt-2">
                            <h3 className="text-sm font-semibold text-theme-muted uppercase tracking-wider border-b border-slate-200 dark:border-slate-700 pb-2">
                                Account Security
                            </h3>

                            <button className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors group">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white dark:bg-slate-900 rounded-lg group-hover:scale-110 transition-transform">
                                        <Shield className="h-4 w-4 text-theme-primary" />
                                    </div>
                                    <div className="text-left">
                                        <p className="text-sm font-medium text-theme-primary">Change Password</p>
                                        <p className="text-xs text-theme-secondary">Update your login password regularly</p>
                                    </div>
                                </div>
                                <span className="text-xs text-primary-blue font-semibold">Update</span>
                            </button>
                        </div>

                        <div className="pt-6 flex justify-end gap-3">
                            {isEditing ? (
                                <>
                                    <button
                                        onClick={() => setIsEditing(false)}
                                        className="px-6 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-theme-secondary hover:bg-slate-50 dark:hover:bg-slate-800 font-medium"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => setIsEditing(false)}
                                        className="px-6 py-2.5 rounded-xl bg-primary-blue text-white hover:bg-blue-600 font-medium shadow-lg shadow-blue-500/20"
                                    >
                                        Save Changes
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="px-6 py-2.5 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:opacity-90 font-medium"
                                >
                                    Edit Profile
                                </button>
                            )}
                        </div>
                    </div>
                </GlassmorphicCard>
            </div>
        </div>
    )
}
